import React, { useEffect, useState } from "react";
import axios from "axios";
import url from "../../url.json";
import { withTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import headers from "../../config/headers";
import { generatePassword } from "../../config/password";
import PasswordMeter from "../../components/PasswordMeter/PasswordMeter";
import ResetShell, { parseError, firstMsg } from "./ResetShell";

const EyeIcon = ({ open }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
    <circle cx="12" cy="12" r="3" />
    {open ? "" : <path d="M3 3l18 18" />}
  </svg>
);

function ForgotPass({ t }) {
  const email = useSelector((state) => state.email);
  const navigate = useNavigate();
  const [form, setForm] = useState({ code: "", password: "", confirm: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [load, setload] = useState(false);
  const [error, setError] = useState();
  const [serverError, setServerError] = useState();

  useEffect(() => {
    if (!email.value) navigate("/forgotpassword");
  }, [email.value, navigate]);

  const setField = (name) => (e) => {
    setForm((f) => ({ ...f, [name]: e.target.value }));
    if (error) setError((err) => ({ ...err, [name === "confirm" ? "password_confirmation" : name]: undefined }));
  };

  const handleGenerate = () => {
    const pwd = generatePassword();
    setForm((f) => ({ ...f, password: pwd, confirm: pwd }));
    setShowPassword(true);
    setError((err) => ({ ...err, password: undefined, password_confirmation: undefined }));
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(pwd).then(
        () => toast.success(t("pw_generated_copied")),
        () => toast.info(t("pw_generated"))
      );
    } else {
      toast.info(t("pw_generated"));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setload(true);
    setServerError();
    const data = {
      email: email.value,
      code: form.code,
      password: form.password,
      password_confirmation: form.confirm,
    };
    axios
      .post(url.url + "/email/verified", data, { headers: headers() })
      .then((res) => {
        toast.success(res.data.data.success);
        setError();
        navigate("/login");
      })
      .catch((err) => {
        const { errors, lines, status } = parseError(err);
        if (status === 401) {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          window.location = "/";
          return;
        }
        setError(errors);
        // maydonga tegishli bo'lmagan xatolar umumiy xabarda
        if (!errors?.code && !errors?.password && !errors?.password_confirmation) setServerError(lines);
        toast.error(lines[0] || t("Password update failed"));
      })
      .finally(() => setload(false));
  };

  return (
    <ResetShell step={1}>
      <h2 className="ln-login__title">{t("Create a password")}</h2>
      <p className="ln-reset__hint">
        {t("Code sent to email:")} <strong>{email.value}</strong>{" "}
        <Link to="/forgotpassword" className="ln-login__link">{t("ln_reset_change_email")}</Link>
      </p>

      <form className="ln-login__form" onSubmit={handleSubmit} noValidate>
        <div className={"ln-field" + (error?.code ? " has-error" : "")}>
          <label htmlFor="resetCode">{t("code_email")}</label>
          <input
            id="resetCode"
            name="code"
            className="ln-reset__code"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="••••••"
            value={form.code}
            onChange={setField("code")}
            autoFocus
          />
          {error?.code ? <i className="ln-login__error">{firstMsg(error.code)}</i> : ""}
        </div>

        <div className={"ln-field" + (error?.password ? " has-error" : "")}>
          <div className="ln-signup__label-row">
            <label htmlFor="resetPassword">{t("New password")}</label>
            <button type="button" className="ln-signup__gen" onClick={handleGenerate}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 12a9 9 0 1 1-3-6.7L21 8" /><path d="M21 3v5h-5" /></svg>
              {t("pw_generate")}
            </button>
          </div>
          <div className="ln-signup__pw">
            <input
              id="resetPassword"
              name="password"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              placeholder={t("New password")}
              value={form.password}
              onChange={setField("password")}
            />
            <button
              type="button"
              className="ln-signup__eye"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={t(showPassword ? "pw_hide" : "pw_show")}
              title={t(showPassword ? "pw_hide" : "pw_show")}
            >
              <EyeIcon open={showPassword} />
            </button>
          </div>
          {error?.password ? <i className="ln-login__error">{firstMsg(error.password)}</i> : ""}
        </div>

        <div className={"ln-field" + (error?.password_confirmation ? " has-error" : "")}>
          <label htmlFor="resetPasswordConfirm">{t("Password confirmation")}</label>
          <div className="ln-signup__pw">
            <input
              id="resetPasswordConfirm"
              name="password_confirmation"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              placeholder={t("Password confirmation")}
              value={form.confirm}
              onChange={setField("confirm")}
            />
            {form.confirm && form.confirm === form.password ? (
              <span className="ln-signup__match" title={t("pw_match")}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12l5 5 9-10" /></svg>
              </span>
            ) : (
              ""
            )}
          </div>
          {error?.password_confirmation ? (
            <i className="ln-login__error">{firstMsg(error.password_confirmation)}</i>
          ) : (
            ""
          )}
        </div>

        <PasswordMeter value={form.password} />

        {serverError ? (
          <div className="ln-reset__alert" role="alert">
            <strong>{t("Password update failed")}</strong>
            {serverError.map((m, i) => (
              <span key={i}>{m}</span>
            ))}
          </div>
        ) : null}

        <button type="submit" className="ln-btn ln-btn_dark ln-login__submit" disabled={load}>
          {load ? t("sending") : t("Update password")}
        </button>
      </form>
    </ResetShell>
  );
}
export default withTranslation()(ForgotPass);
